type UnionToIntersection<U> = (
  U extends unknown ? (arg: U) => void : never
) extends (arg: infer I) => void
  ? I
  : never;

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Recursively merges plain-object sources into a new object. Later sources win
 * on leaf collisions; nested objects are merged rather than replaced.
 *
 * Used to reassemble the `organizations` namespace from its topic files. The
 * return type is the intersection of all sources, so `Paths<>`-based key typing
 * (see utils.ts) still resolves every nested translation key.
 */
export const deepMerge = <T extends Record<string, unknown>[]>(
  ...sources: T
): UnionToIntersection<T[number]> =>
  sources.reduce<Record<string, unknown>>((acc, src) => {
    for (const [key, val] of Object.entries(src)) {
      acc[key] =
        isObject(acc[key]) && isObject(val) ? deepMerge(acc[key], val) : val;
    }

    return acc;
  }, {}) as UnionToIntersection<T[number]>;
